// Libraries
import { Box, Flex, Text } from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
// Components
import { IconFile } from './Components';
// Utils
import text from '@translations/en.json';
// Types
import { DropzoneProps } from './Dropzone.types';

/**
 * Dropzone component for handling file drops.
 *
 * @component
 * @param {DropzoneProps} props - The component props.
 * @param {Function} props.onDropAccepted - The function to handle accepted files.
 * @param {Function} props.onDropRejected - The function to handle rejected files.
 * @param {string | string[]} props.accept - The accepted file types or MIME types.
 * @param {boolean} props.multiple - Indicates whether multiple files can be dropped or not.
 * @param {ReactNode} props.children - The content to be displayed within the Dropzone component.
 * @param {boolean} props.showAcceptedFiles - Indicates whether to show accepted files or not.
 * @param {boolean} props.disabled - Indicates whether the Dropzone component is disabled or not.
 * @param {File[]} props.acceptedFiles - The array of accepted files.
 * @returns {ReactElement} The Dropzone component.
 */
export const Dropzone = ({
  onDropAccepted,
  onDropRejected,
  accept,
  multiple,
  children,
  showAcceptedFiles,
  disabled,
  acceptedFiles,
  ...flexProps
}: DropzoneProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept,
    multiple,
    disabled,
  });

  const { borderColor, backgroundColor, cursor } = isDragActive
    ? {
        borderColor: 'base',
        cursor: 'grabbing',
        backgroundColor: 'aqua',
      }
    : {
        borderColor: 'grey.20',
        cursor: 'pointer',
        backgroundColor: 'snow',
      };

  return (
    <Box {...getRootProps()} w='full'>
      <input {...getInputProps()} />
      <Flex
        border='2px dashed'
        borderColor={borderColor}
        borderRadius='md'
        cursor={cursor}
        backgroundColor={backgroundColor}
        flexDirection='column'
        {...flexProps}
      >
        {children}
        {acceptedFiles && acceptedFiles.length > 0 && (
          <Flex flexDirection='column' rowGap={2} mt={7}>
            {acceptedFiles.map((file) => {
              return (
                <Flex columnGap={2} alignItems='center' justifyContent='center' key={file.name}>
                  <IconFile contentType={file.type} />
                  <Text variant='label'>{file.name}</Text>
                </Flex>
              );
            })}
          </Flex>
        )}
        {showAcceptedFiles && !acceptedFiles && (
          <Box textAlign='center' mt={7}>
            {Object.keys(accept).map((key) => {
              return (
                <Text variant='caption' key={key}>
                  {`${accept[key].join(' | ')} ${text.format_required}`}
                </Text>
              );
            })}
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default Dropzone;
